import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, CopyCheck } from "lucide-react";
import { toast } from "sonner";
import FairQuickCard from "@/components/fair/FairQuickCard";
import { tl, itemAmount } from "@/lib/fair";

const NEW = "__new__";
const sel = "h-10 bg-[#FFFBF4] border-[#DCCFB8]";
const num = "h-10 text-center font-mono bg-[#FFFBF4] border-[#DCCFB8]";
const content = "bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118]";
const newItem = "text-[#B45309] font-semibold";

// One model inside a fair order: model dropdown, then one compact line per color
export default function FairModelBlock({ idx, rows, products, usedModels, onModel, onRow, onAddColor, onRemoveRow, onRemoveBlock, onCreated }) {
  const [newModel, setNewModel] = useState(false);
  const [newColorRow, setNewColorRow] = useState(null);
  const model = rows[0].model;
  const codes = [...new Set(products.map((p) => p.model_code))].filter((c) => !usedModels.includes(c));
  const colors = products.filter((p) => p.model_code === model);
  const total = rows.reduce((a, r) => a + (parseInt(r.koli) || 0), 0);
  const p = `fair-block-${idx}`;

  return (
    <div className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-3" data-testid={p}>
      <div className="flex items-center gap-2 mb-2">
        <Select value={model} onValueChange={(v) => (v === NEW ? setNewModel(true) : onModel(v))}>
          <SelectTrigger data-testid={`${p}-model`} className={`${sel} h-11 flex-1 sm:max-w-xs font-mono font-bold`}>
            <SelectValue placeholder="Model kodu seçin..." />
          </SelectTrigger>
          <SelectContent className={content}>
            {codes.map((c) => <SelectItem key={c} value={c} data-testid={`${p}-model-option-${c}`}>{c}</SelectItem>)}
            {codes.length > 0 && <SelectSeparator />}
            <SelectItem value={NEW} className={newItem} data-testid={`${p}-model-option-new`}>+ Yeni model oluştur</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs font-mono text-[#6B5B4A]" data-testid={`${p}-total`}>{total} koli</span>
        {onRemoveBlock && <button type="button" onClick={onRemoveBlock} data-testid={`${p}-remove`} title="Modeli kaldır" className="text-[#8C7B68] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>}
      </div>
      <FairQuickCard open={newModel} onClose={() => setNewModel(false)} testId={`${p}-new-model`}
        onCreated={(card) => onCreated(card, rows[0].id, true)} />

      {model && (
        <div className="space-y-1.5 mt-2">
          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_80px_80px_110px_120px_20px] gap-2 px-1 text-[10px] font-mono uppercase text-[#6B5B4A]">
            <span>Renk</span><span>Koli</span><span>Koli içi</span><span>Çift fiyatı ₺</span><span className="text-right">Tutar</span><span />
          </div>
          {rows.map((r, i) => {
            const opts = colors.filter((c) => c.id === r.fair_product_id || !rows.some((x) => x.fair_product_id === c.id));
            return (
              <div key={r.id} data-testid={`${p}-row-${i}`}>
                <div className="grid grid-cols-2 sm:grid-cols-[minmax(0,1fr)_80px_80px_110px_120px_20px] gap-2 items-center">
                  <Select value={r.fair_product_id} onValueChange={(v) => (v === NEW ? setNewColorRow(r.id) : onRow(r.id, { fair_product_id: v }))}>
                    <SelectTrigger data-testid={`${p}-color-${i}`} className={`${sel} col-span-2 sm:col-span-1`}><SelectValue placeholder="Renk seçin..." /></SelectTrigger>
                    <SelectContent className={content}>
                      {opts.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`${p}-color-${i}-option-${c.id}`}>{c.model_color}</SelectItem>)}
                      {opts.length > 0 && <SelectSeparator />}
                      <SelectItem value={NEW} className={newItem} data-testid={`${p}-color-${i}-option-new`}>+ Yeni renk oluştur</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" min={1} inputMode="numeric" placeholder="Koli" value={r.koli} onChange={(e) => onRow(r.id, { koli: e.target.value })}
                    data-testid={`${p}-koli-${i}`} className={`${num} font-bold`} />
                  <Input type="number" min={1} inputMode="numeric" placeholder="Çift" value={r.pairs} onChange={(e) => onRow(r.id, { pairs: e.target.value })}
                    data-testid={`${p}-pairs-${i}`} className={num} />
                  <Input type="number" min={0} step="0.01" inputMode="decimal" placeholder="₺" value={r.price} onChange={(e) => onRow(r.id, { price: e.target.value })}
                    data-testid={`${p}-price-${i}`} className={num} />
                  <span className="text-right font-mono text-sm font-bold text-[#B45309]" data-testid={`${p}-amount-${i}`}>{tl(itemAmount(r))}</span>
                  {rows.length > 1
                    ? <button type="button" onClick={() => onRemoveRow(r.id)} data-testid={`${p}-row-remove-${i}`} title="Rengi kaldır" className="text-[#8C7B68] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
                    : <span />}
                </div>
                <FairQuickCard model={model} open={newColorRow === r.id} onClose={() => setNewColorRow(null)} testId={`${p}-new-color-${i}`}
                  onCreated={(card) => onCreated(card, r.id, false)} />
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="ghost" onClick={onAddColor} data-testid={`${p}-add-color`}
              className="h-8 px-2 text-[#B45309] hover:bg-[#FFFBF4] hover:text-[#B45309]">
              <Plus className="w-4 h-4 mr-1" /> Renk Ekle
            </Button>
            {rows.length > 1 && rows[0].price !== "" && (
              <Button type="button" variant="ghost" data-testid={`${p}-apply-price`}
                onClick={() => { rows.slice(1).forEach((r) => onRow(r.id, { price: rows[0].price })); toast.success(`${tl(rows[0].price)} tüm renklere uygulandı`); }}
                className="h-8 px-2 text-[#B45309] hover:bg-[#FFFBF4] hover:text-[#B45309]">
                <CopyCheck className="w-4 h-4 mr-1" /> İlk fiyatı ({tl(rows[0].price)}) tüm renklere uygula
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
